import {
  Icon,
  IconProps,
  IconSource,
  InlineStack,
  LegacyStack,
  Modal,
  Tooltip,
} from '@shopify/polaris'
import { AlertCircleIcon, InfoIcon } from '@shopify/polaris-icons'
import React from 'react'

type WorkflowTagVariableTooltip = {
  content: string
  type: 'info' | 'caution'
}

const tooltipIcons: Record<
  WorkflowTagVariableTooltip['type'],
  { source: IconSource; tone: IconProps['tone'] }
> = {
  info: { source: InfoIcon, tone: 'base' },
  caution: { source: AlertCircleIcon, tone: 'caution' },
}

const multipleAnchorsTooltip: WorkflowTagVariableTooltip = {
  content: 'Variable not supported on plans with multiple anchors',
  type: 'caution',
}

const basicWorkflowTagVariablesTooltips: Record<string, WorkflowTagVariableTooltip | null> = {
  subscription_id: null,
  subscription_created_at: null,
  subscription_created_at_day: null,
  subscription_created_at_month: null,
  subscription_created_at_year: null,
  subscription_initial_order_number: null,
  billing_interval_type: null,
  billing_interval_count: null,
  billing_anchor: multipleAnchorsTooltip,
  billing_anchor_weekday: multipleAnchorsTooltip,
  billing_anchor_day_of_month: multipleAnchorsTooltip,
  billing_anchor_month: multipleAnchorsTooltip,
  delivery_interval_count: null,
}

const basicWorkflowTagVariables = Object.keys(basicWorkflowTagVariablesTooltips)

const orderWorkflowTagVariablesTooltips: Record<string, WorkflowTagVariableTooltip | null> = {
  payment_number: null,
  subscription_item_quantity: {
    content: 'Total quantity of subscription items in the order',
    type: 'info',
  },
  subscription_line_item_count: {
    content: 'Number of separate subscription line items in the order',
    type: 'info',
  },
}

const orderWorkflowTagVariables = Object.keys(orderWorkflowTagVariablesTooltips)

const VariablesStack = ({
  variables,
  tooltips,
}: {
  variables: string[]
  tooltips: Record<string, WorkflowTagVariableTooltip | null>
}) => (
  <LegacyStack vertical spacing="extraTight">
    {variables.map((variable) => {
      const tooltip = tooltips[variable]

      return (
        <InlineStack key={`variable-${variable}`} gap="200" blockAlign="center">
          <code style={{ background: '#ebeef0', padding: '0.1em' }}>{`{{${variable}}}`}</code>
          {tooltip && (
            <Tooltip content={tooltip.content} dismissOnMouseOut={false}>
              <Icon {...tooltipIcons[tooltip.type]} />
            </Tooltip>
          )}
        </InlineStack>
      )
    })}
  </LegacyStack>
)

export const WorkflowVariablesModal = ({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => any
}) => (
  <Modal open={open} onClose={onClose} sectioned title="Variables">
    <LegacyStack vertical>
      <p>You can use the following variables in your tags:</p>
      <VariablesStack
        variables={basicWorkflowTagVariables}
        tooltips={basicWorkflowTagVariablesTooltips}
      />
      <p>
        The following ones describe the order and are available only on{' '}
        <b>Subscription is created</b>, <b>Subscription payment succeeds</b> and{' '}
        <b>Future subscription price change applied</b> events. A tag using them on any other event
        is skipped:
      </p>
      <VariablesStack
        variables={orderWorkflowTagVariables}
        tooltips={orderWorkflowTagVariablesTooltips}
      />
    </LegacyStack>
  </Modal>
)
